/**
 * A camera the scanner can open. Matches Dynamsoft `VideoDeviceInfo` without importing the SDK.
 */
export interface ICameraPreviewDevice {
  deviceId: string;
  label: string;
}

/**
 * Ranks a camera label so a normal rear camera is preferred over front, wide, or depth cameras.
 * Higher is better. Negative scores are cameras that should not be opened for barcode scanning.
 *
 * @param {string} label - Device label reported by the browser.
 * @returns {number} Rank score.
 */
export function rankRearCameraLabel(label: string): number {
  const name = label.toLowerCase();
  if (/front|user|face|selfie/.test(name)) {
    return -1;
  }
  let score = name.length === 0 ? 1 : 0;
  if (/back|rear|environment|world/.test(name)) {
    score += 10;
  }
  if (/wide|ultra|macro|depth|tele|infrared|\bir\b/.test(name)) {
    score -= 6;
  }
  return score;
}

/**
 * Picks the rear camera most likely to produce a normal colour preview.
 *
 * @param {ICameraPreviewDevice[]} cameras - Cameras reported by the device.
 * @returns {ICameraPreviewDevice | undefined} Preferred camera, when one is suitable.
 */
export function pickPreferredRearCamera(
  cameras: ICameraPreviewDevice[],
): ICameraPreviewDevice | undefined {
  return [...cameras]
    .filter((camera: ICameraPreviewDevice) => rankRearCameraLabel(camera.label) >= 0)
    .sort(
      (left: ICameraPreviewDevice, right: ICameraPreviewDevice) =>
        rankRearCameraLabel(right.label) - rankRearCameraLabel(left.label),
    )[0];
}

/**
 * Picks a different rear camera after the current preview stays blank or green.
 *
 * @param {ICameraPreviewDevice[]} cameras - Cameras reported by the device.
 * @param {string | undefined} currentDeviceId - Camera that failed to show a feed.
 * @returns {ICameraPreviewDevice | undefined} Next camera to try.
 */
export function pickAlternateRearCamera(
  cameras: ICameraPreviewDevice[],
  currentDeviceId: string | undefined,
): ICameraPreviewDevice | undefined {
  return [...cameras]
    .filter(
      (camera: ICameraPreviewDevice) =>
        camera.deviceId !== currentDeviceId && rankRearCameraLabel(camera.label) >= 0,
    )
    .sort(
      (left: ICameraPreviewDevice, right: ICameraPreviewDevice) =>
        rankRearCameraLabel(right.label) - rankRearCameraLabel(left.label),
    )[0];
}

/**
 * Detects a flat green frame. That is the preview Dynamsoft draws when the video texture never starts.
 *
 * @param {Uint8ClampedArray} pixels - RGBA samples from a downscaled video frame.
 * @returns {boolean} True when the frame is a solid green field.
 */
export function isSolidGreenSample(pixels: Uint8ClampedArray): boolean {
  const channels = 4;
  const count = Math.floor(pixels.length / channels);
  if (count === 0) {
    return false;
  }
  let sumRed = 0;
  let sumGreen = 0;
  let sumBlue = 0;
  let minLuminance = 255;
  let maxLuminance = 0;
  for (let index = 0; index < pixels.length; index += channels) {
    const red = pixels[index];
    const green = pixels[index + 1];
    const blue = pixels[index + 2];
    sumRed += red;
    sumGreen += green;
    sumBlue += blue;
    const luminance = (red + green + blue) / 3;
    if (luminance < minLuminance) {
      minLuminance = luminance;
    }
    if (luminance > maxLuminance) {
      maxLuminance = luminance;
    }
  }
  const flat = maxLuminance - minLuminance < 10;
  if (!flat) {
    return false;
  }
  const avgRed = sumRed / count;
  const avgGreen = sumGreen / count;
  const avgBlue = sumBlue / count;
  return avgGreen > 70 && avgGreen > avgRed * 1.35 && avgGreen > avgBlue * 1.35;
}
